import { useState } from "react";
import { motion } from "framer-motion";
import NetworkIcon from "@core/network-icon";
import { AIFlowchartContent } from "../../types/ai-templates";

// Import the Google Fonts (Roboto) as specified in the SVG
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=block');
`;

export function FlowchartTemplate3({
  content,
  onUpdate,
}: {
  content: AIFlowchartContent;
  onUpdate: (newContent: AIFlowchartContent) => void;
}) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<
    "title" | "description" | null
  >(null);
  const [tempValue, setTempValue] = useState("");

  // Handle step update
  const handleStepUpdate = (
    index: number,
    field: "title" | "description",
    newText: string,
  ) => {
    const newContent = JSON.parse(JSON.stringify(content));
    if (newContent.steps && newContent.steps[index]) {
      newContent.steps[index][field] = newText;
      onUpdate(newContent);
    }
  };

  // Ensure at least three steps exist (though we'll use up to 4 as per the data)
  if (!content.steps || content.steps.length < 3) {
    return (
      <div className="p-4 text-center text-error">
        Invalid flowchart data structure: At least 3 steps required.
      </div>
    );
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
      },
    },
  };

  return (
    <>
      {/* Inject Google Fonts */}
      <style>{styles}</style>
      {/* SVG Definitions for Patterns */}
      <svg style={{ position: "absolute", width: 0, height: 0 }}>
        <defs>
          <pattern
            id="crawlingPattern-2196F399"
            x="0"
            y="0"
            width="12"
            height="12"
            patternUnits="userSpaceOnUse"
            fill="#2196F399"
          >
            <use href="#crawlingSymbol"></use>
          </pattern>
          <pattern
            id="crawlingPattern-FF980099"
            x="0"
            y="0"
            width="12"
            height="12"
            patternUnits="userSpaceOnUse"
            fill="#FF980099"
          >
            <use href="#crawlingSymbol"></use>
          </pattern>
          <pattern
            id="crawlingPattern-c1c1c11a"
            x="0"
            y="0"
            width="12"
            height="12"
            patternUnits="userSpaceOnUse"
            fill="#c1c1c11a"
          >
            <use href="#crawlingSymbol"></use>
          </pattern>
          <pattern
            id="crawlingPattern-ffffff38"
            x="0"
            y="0"
            width="12"
            height="12"
            patternUnits="userSpaceOnUse"
            fill="#ffffff38"
          >
            <use href="#crawlingSymbol"></use>
          </pattern>
          <pattern
            id="crawlingPattern-5f5f5f1a"
            x="0"
            y="0"
            width="12"
            height="12"
            patternUnits="userSpaceOnUse"
            fill="#5f5f5f1a"
          >
            <use href="#crawlingSymbol"></use>
          </pattern>
          <symbol id="crawlingSymbol">
            <g>
              <animateTransform
                attributeName="transform"
                type="translate"
                from="0 0"
                to="-12 0"
                dur="0.4s"
                repeatCount="indefinite"
              />
              <path d="M -12.5 12.5 l 13 -13 l 6 0 l -13 13 Z M -0.5 12.5 l 13 -13 l 6 0 l -13 13 Z M 11.5 12.5 l 13 -13 l 6 0 l -13 13 Z" />
            </g>
          </symbol>
        </defs>
      </svg>

      {/* Container with 100% width, max-width 1100px, and centered */}
      <div className="w-full flex justify-center">
        <motion.div
          className="relative p-10"
          style={{
            width: "100%",
            maxWidth: "1100px",
            fontFamily: "Roboto, sans-serif",
            height: 540,
          }}
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Background Pattern Overlay */}
          <div
            className="absolute inset-0"
            style={{ fill: "url(#crawlingPattern-5f5f5f1a)" }}
          >
            <svg width="100%" height="516" preserveAspectRatio="xMidYMid meet">
              <path d="M 0 0 L 1100 0 L 1100 516 L 0 516 Z" />
            </svg>
          </div>

          {/* Main Title - Centered */}
          <div className="absolute left-1/2 transform text-xl text-gray-700 font-semibold -translate-x-1/2 z-40">
            {content.title || "İyonik Bağ Oluşumu"}
          </div>

          {/* Steps */}
          {content.steps.slice(0, 4).map((step, index) => {
            const isEditingTitle =
              editingIndex === index && editingField === "title";
            const isEditingDescription =
              editingIndex === index && editingField === "description";

            // Calculate x positions dynamically for up to 4 steps within 1100px
            const totalWidth = 1100 - 20; // Account for padding (10px on each side)
            const stepWidth = 224;
            const gap = (totalWidth - stepWidth * 4) / 3; // Space between steps
            const xPositions = Array.from(
              { length: 4 },
              (_, i) => i * (stepWidth + gap) + 10, // Start at padding 10px
            );

            const baseColor = step.color;
            const pattern = `crawlingPattern-${
              content.genericColors[
                index % content.genericColors.length
              ].replace("#", "") + "99"
            }`;

            return (
              <motion.div
                key={index}
                className="absolute"
                style={{ left: xPositions[index], top: 122 }}
                variants={itemVariants}
              >
                {/* Upper Rectangle (Main Box) */}
                <svg width="224" height="212">
                  <g>
                    <path
                      d="M 10 10 L 214 10 L 214 202 L 10 202 Z"
                      fill={baseColor}
                    />
                    <path
                      d="M 10 10 L 214 10 L 214 202 L 10 202 Z"
                      fill={`url(#${pattern})`}
                    />
                    {/* Bottom Stroke */}
                    <path
                      d="M 23 201 L 201 201"
                      stroke="#2c2c2c"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeMiterlimit="4"
                    />
                    <path
                      d="M 23 201 L 201 201"
                      stroke="#ffffff29"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeMiterlimit="4"
                      strokeDasharray="4,6"
                    >
                      <animate
                        attributeName="stroke-dashoffset"
                        values="30;0"
                        dur="1s"
                        repeatCount="indefinite"
                        begin="0"
                      />
                    </path>
                  </g>
                </svg>

                {/* Icon - Centered in the Upper Box */}
                <div
                  className="absolute"
                  style={{
                    left: (224 - 140) / 2, // Center the 140px wide icon in the 224px wide box
                    top: (212 - 140) / 2, // Center vertically in the 212px tall box
                  }}
                >
                  {step.iconId ? (
                    <svg width="140" height="140">
                      <foreignObject x="50" y="50" width="40" height="40">
                        <NetworkIcon
                          value={step.icon_description ?? step.iconId}
                          type="LUCIDE"
                          className="w-10 h-10 text-white"
                        />
                      </foreignObject>
                    </svg>
                  ) : (
                    <svg width="140" height="140">
                      <rect width="140" height="140" fill="#f4f4f4" />
                    </svg>
                  )}
                </div>

                {/* Lower Rectangle (Text Box) */}
                <div className="absolute" style={{ left: 0, top: 314 - 122 }}>
                  <svg width="224" height="188">
                    <g>
                      <path
                        d="M 10 10 L 214 10 L 214 34 L 10 34 Z M 10 34 L 214 34 L 214 154 L 10 154 Z M 10 154 L 214 154 L 214 178 L 10 178 Z"
                        fill={baseColor}
                      />
                      <path
                        d="M 10 10 L 214 10 L 214 34 L 10 34 Z M 10 34 L 214 34 L 214 154 L 10 154 Z M 10 154 L 214 154 L 214 178 L 10 178 Z"
                        fill={`url(#${pattern})`}
                      />
                    </g>
                  </svg>
                </div>

                {/* Title - Centered in the Text Box */}
                <div
                  className="absolute text-center cursor-pointer"
                  style={{
                    left: 0,
                    width: 224,
                    top: 338 - 122,
                  }}
                >
                  {isEditingTitle ? (
                    <input
                      autoFocus
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      onBlur={() => {
                        setEditingIndex(null);
                        setEditingField(null);
                        if (tempValue !== step.title) {
                          handleStepUpdate(index, "title", tempValue);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                      }}
                      className="text-[#2c2c2c] text-[20px] font-bold bg-transparent outline-none w-full text-center"
                      style={{ fontFamily: "Roboto, sans-serif" }}
                    />
                  ) : (
                    <span
                      onClick={() => {
                        setEditingIndex(index);
                        setEditingField("title");
                        setTempValue(step.title);
                      }}
                      className="text-[#2c2c2c] text-[20px] font-bold cursor-pointer"
                      style={{ fontFamily: "Roboto, sans-serif" }}
                    >
                      {step.title}
                    </span>
                  )}
                </div>

                {/* Description - Centered Below the Title */}
                <div
                  className="absolute text-center cursor-pointer"
                  style={{
                    left: 10,
                    width: 200,
                    top: 374 - 122,
                  }}
                >
                  {isEditingDescription ? (
                    <textarea
                      autoFocus
                      value={tempValue}
                      onChange={(e) => setTempValue(e.target.value)}
                      onBlur={() => {
                        setEditingIndex(null);
                        setEditingField(null);
                        if (tempValue !== step.description) {
                          handleStepUpdate(index, "description", tempValue);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.height = "auto";
                        e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                      }}
                      className="text-[#2c2c2c] text-[15px] bg-transparent outline-none w-full text-center resize-none"
                      style={{ fontFamily: "Roboto, sans-serif" }}
                      rows={3}
                    />
                  ) : (
                    <span
                      onClick={() => {
                        setEditingIndex(index);
                        setEditingField("description");
                        setTempValue(step.description);
                      }}
                      className="text-[#2c2c2c] text-[15px] cursor-text max-w"
                      style={{
                        fontFamily: "Roboto, sans-serif",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {step.description}
                    </span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </>
  );
}
